import { useEffect, useMemo, useRef, useState } from "react";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  curvedPath,
  etaMinutes,
  formatDistance,
  haversineKm,
  hostStartPoint,
  type LatLng,
} from "@/lib/geo";

interface MeetupMapProps {
  pickup: LatLng;
  pickupLabel: string;
  hostName: string;
  riderName: string;
  live: boolean;
  seed?: number;
}

function pinIcon(kind: "car" | "rider" | "pickup"): L.DivIcon {
  const glyph = kind === "car" ? "ri-roadster-fill" : kind === "rider" ? "ri-user-3-fill" : "ri-flag-2-fill";
  return L.divIcon({
    className: "gari-pin-wrap",
    html: `<span class="gari-pin gari-pin--${kind}"><i class="${glyph}"></i></span>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

export default function MeetupMap({ pickup, pickupLabel, hostName, riderName, live, seed = 7 }: MeetupMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const carMarkerRef = useRef<L.Marker | null>(null);

  const start = useMemo(() => hostStartPoint(pickup, seed), [pickup.lat, pickup.lng, seed]);
  const path = useMemo(() => curvedPath(start, pickup, 60), [start.lat, start.lng, pickup.lat, pickup.lng]);
  const [step, setStep] = useState(2);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return undefined;

    const map = L.map(el, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
    });
    mapRef.current = map;

    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: "&copy; OpenStreetMap &copy; CARTO",
      subdomains: "abcd",
      maxZoom: 20,
    }).addTo(map);

    // Planned route — dashed line from the host's start towards the pickup point.
    const routeLine = L.polyline(
      path.map((point) => [point.lat, point.lng] as L.LatLngTuple),
      { color: "#e2a13a", weight: 3, opacity: 0.55, dashArray: "6 8" }
    ).addTo(map);

    // Pickup point (the client's location).
    L.marker([pickup.lat, pickup.lng], { icon: pinIcon("pickup") })
      .addTo(map)
      .bindTooltip(`Pickup · ${pickupLabel}`, { direction: "top", offset: [0, -14] });

    // Rider marker sits at the pickup point.
    L.marker([pickup.lat, pickup.lng], { icon: pinIcon("rider") })
      .addTo(map)
      .bindTooltip(`${riderName} · waiting`, { direction: "right", offset: [16, 0] });

    // Host car marker starts along the route.
    const first = path[Math.min(2, path.length - 1)];
    const carMarker = L.marker([first.lat, first.lng], { icon: pinIcon("car") })
      .addTo(map)
      .bindTooltip(`${hostName} · on the way`, { direction: "top", offset: [0, -14] });
    carMarkerRef.current = carMarker;

    map.fitBounds(routeLine.getBounds().pad(0.35));

    window.setTimeout(() => map.invalidateSize(), 260);

    return () => {
      map.remove();
      mapRef.current = null;
      carMarkerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!live) {
      setStep(path.length - 1);
      return undefined;
    }
    setStep(2);
    const timer = window.setInterval(() => {
      setStep((prev) => (prev >= path.length - 1 ? prev : prev + 1));
    }, 1500);
    return () => window.clearInterval(timer);
  }, [live, path]);

  useEffect(() => {
    const marker = carMarkerRef.current;
    if (!marker) return;
    const point = path[Math.min(step, path.length - 1)];
    marker.setLatLng([point.lat, point.lng]);
  }, [step, path]);

  const current = path[Math.min(step, path.length - 1)];
  const remaining = haversineKm(current, pickup);
  const arrived = remaining < 0.05;

  return (
    <div className="overflow-hidden rounded-2xl border border-background-200/70 bg-background-100">
      <div className="relative h-[300px] w-full md:h-[360px]">
        <div ref={containerRef} className="h-full w-full" />

        <div className="pointer-events-none absolute left-3 top-3 z-[500] rounded-xl border border-background-200/70 bg-background-50/85 px-3.5 py-2.5 backdrop-blur">
          <p className="flex items-center gap-2 text-xs font-medium text-foreground-900">
            <span className={`flex h-2 w-2 rounded-full ${arrived ? "bg-accent-400" : "bg-primary-400 animate-pulse"}`}></span>
            {arrived ? "Host has arrived" : "Live tracking"}
          </p>
          <p className="mt-1 text-xs text-foreground-600">
            {arrived ? `Meet ${hostName} at the pickup point` : `${hostName} · ${formatDistance(remaining)} · ~${etaMinutes(remaining)} min`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 divide-x divide-background-200/70 border-t border-background-200/70">
        <div className="p-4">
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-foreground-500">
            <i className="ri-roadster-line text-primary-500"></i> Driver
          </p>
          <p className="mt-1 truncate text-sm font-medium text-foreground-950">{hostName}</p>
        </div>
        <div className="p-4">
          <p className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-foreground-500">
            <i className="ri-user-3-line text-accent-400"></i> Client
          </p>
          <p className="mt-1 truncate text-sm font-medium text-foreground-950">{riderName}</p>
        </div>
      </div>
    </div>
  );
}